import { Dispatch, SetStateAction } from "react";
import { Box, Button, Center, Flex, Grid, GridItem, Heading, HStack, IconButton, Image, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, SimpleGrid, Spinner, Text, useDisclosure, VStack } from "@chakra-ui/react";
import { CloseIcon, SettingsIcon } from "@chakra-ui/icons";

import { DaySearch, ServerStatus } from "../../utils/interfaces";
import ServerClock from "./ServerClock";
import firmProLogo from "../../assets/firmpro_logo.svg";
import { useNavigate } from "react-router";
import GeneralConfigForm from "./GeneralConfigForm";
import { postLogout } from "../../utils/api";

interface HeaderProps {
  softwareVersion: string;
  serverStatus: ServerStatus;
  numEnabledFirmPros: number;
  numBadFirmPros: number;
  daySearch: DaySearch;
  setDaySearch: Dispatch<SetStateAction<DaySearch>>;
}

/**
 * Muestra el logo de FirmPro, el estado del servidor, una opción para buscar
 * fallas en el pasado y un recuento de equipos en buen estado y con fallas.
 * 
 * @param {HeaderProps} 
 * @returns {JSX.Element}
 */
export default function Header({
  softwareVersion,
  serverStatus,
  numEnabledFirmPros,
  numBadFirmPros,
  daySearch,
  setDaySearch,
}: HeaderProps): JSX.Element {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();

  const logout = () => {
    postLogout()
      .then(_ => navigate("/login"))
      .catch(err => console.log(err))
  };

  let localSearchDays = daySearch.days;

  const onChangeFloat = (event: React.ChangeEvent<HTMLInputElement>) => {
    // event.target se refiere al input en sí (Input, TimeInput, Radio)
    localSearchDays = parseFloat(event.target.value);
  };

  const onPressEnter = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      searchRange();
    }
  };

  const searchRange = () => {
    // El atributo isLoading cambiará en HomeScreen cuando se termine de cargar los datos.
    setDaySearch({ enabled: true, isLoading: true, days: localSearchDays });
  };

  const cancelSearch = () => {
    setDaySearch({ enabled: false, isLoading: true, days: localSearchDays });
  };

  return (
    <VStack w="full" spacing={3}>

      {/*
      Título, logo, botón para cerrar sesión e indicador de "modo" de búsqueda
      */}
      <Grid
        templateColumns="repeat(2, auto)"
        templateRows="repeat(3, auto)"
        columnGap={{ "base": 2, "md": 2, "lg": 4 }}
        rowGap={2}
        justifyItems="center"
        alignItems="center"
      >
        <GridItem rowSpan={2} justifyItems="center">
          <a href="https://smartgrid.uc.cl/" target="_blank" rel="noreferrer">
            <Image
              w={{ "base": "140px", "sm": "180px", "md": "230px", "lg": "260px" }}
              src={firmProLogo}
            />
          </a>
        </GridItem>
        <GridItem>
          <Button onClick={logout} colorScheme="red" shadow="lg">Cerrar sesión</Button>
        </GridItem>
        <GridItem>
          <Center w="134px" py="2" mr={{ "base": "32px", "md": 0 }} borderRadius="md" bg={daySearch.enabled ? "orange.100" : "cyan.100"}>
            Modo: {daySearch.enabled ? "Normal" : "Light"}
          </Center>
        </GridItem>
        <GridItem>
          <Heading textAlign="center" as="h1" size="lg">
            FirmPro Semáforo v{softwareVersion}
          </Heading>
        </GridItem>
        <GridItem>
          <Button onClick={onOpen} aria-label="Configuración del equipo">
            Configuración&ensp;
            <SettingsIcon />
          </Button>
          <Modal
            size="xl"
            scrollBehavior="inside"
            isOpen={isOpen}
            onClose={onClose}
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                Configuración general
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <GeneralConfigForm onClose={onClose} />
              </ModalBody>
            </ModalContent>
          </Modal>
        </GridItem>
      </Grid>

      {/*
      Información del servidor
      Acá se muestra la hora (serverTime) y si hubo error (hasError)
      También hay un botón para seleccionar un rango de días de búsqueda
      */}
      <SimpleGrid columns={{ "base": 1, "md": 2 }} rowGap="2" columnGap={{ "md": 4, "lg": 12 }}>
        <ServerClock serverStatus={serverStatus} />
        <Flex gap="4">
          <Center>
            {daySearch.enabled
              ? daySearch.isLoading
                ? <Spinner color="teal"></Spinner>
                : <IconButton aria-label="Cancelar" colorScheme="red" icon={<CloseIcon />} onClick={cancelSearch} shadow="lg" />
              : <IconButton aria-label="Cancelar" isDisabled={true} colorScheme="gray" icon={<CloseIcon />} shadow="lg" />}
          </Center>
          <Input name="searchDays" type="number" defaultValue={daySearch.days} onChange={onChangeFloat} onKeyDown={onPressEnter}></Input>
          <Button px="6" colorScheme={daySearch.isLoading ? "gray" : "cyan"} isDisabled={daySearch.isLoading} onClick={searchRange} shadow="lg">
            Buscar
          </Button>
        </Flex>
      </SimpleGrid>

      {/*
      Información general de equipos
      Se muestra la cantidad de equipos en buen estado (numFirmPros - numBadFirmPros)
      y la cantidad de equipos rojos y amarillos (numBadFirmPros)
      */}
      <SimpleGrid columns={{ "base": 1, "md": 2 }} spacing={{ "base": 2, "md": 3, "lg": "114px" }}>
        {/* Equipos conectados */}
        <HStack spacing="0" align="center" shadow="sm">
          <Box px={{ "base": 2, "sm": 5, "md": 4, "lg": 8 }} py="1" borderRadius="md" bg="green.500" color="white">
            <Text fontSize="xl"><b>Equipos en buen estado:</b></Text>
          </Box>
          <Text pl="4" fontSize="xl"><b>{numEnabledFirmPros - numBadFirmPros}</b></Text>
        </HStack>

        {/* Equipos con fallas */}
        <HStack spacing="0" align="center" shadow="sm">
          <Box pl={{ "base": 2, "sm": 5, "md": 4, "lg": 8 }} pr="0" py="1" borderLeftRadius="md" bg="yellow.500" color="white">
            <Text fontSize="xl"><b>Equipos&nbsp;</b></Text>
          </Box>
          <Box pl="0" pr={{ "base": 2, "sm": 5, "md": 4, "lg": 8 }} py="1" borderRightRadius="md" bg="red.500" color="white">
            <Text fontSize="xl"><b>con fallas:</b></Text>
          </Box>
          <Text pl="4" fontSize="xl"><b>{numBadFirmPros}</b></Text>
        </HStack>
      </SimpleGrid>
    </VStack>
  );
}
